import { useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import {
  commentsHistoryPeriodAtom,
  commentsHistoryTypeAtom,
  reatomCommentsHistory,
} from '@root/features/comment/atoms'
import { CommentsHistory } from '@root/features/comment/ui'
import { useTranslation } from '@root/shared/localization'
import { Period } from '@root/shared/period'
import { SegmentControl } from '@root/shared/ui-kit'

import type { Comment } from '@root/entities/comment'
import type { DelugeId } from '@root/entities/user-short'

type ProfileBoardCommentsProps = {
  delugeId: DelugeId
}

export const ProfileBoardComments = ({
  delugeId,
}: ProfileBoardCommentsProps) => {
  const { t } = useTranslation()
  const [network] = useAtom(selectedNetworkAtom)
  const [type, setType] = useAtom(commentsHistoryTypeAtom)
  const [period, setPeriod] = useAtom(commentsHistoryPeriodAtom)

  const model = useMemo(() => {
    // eslint-disable-next-line @reatom/unit-naming-rule
    return reatomCommentsHistory({
      delugeId,
      type,
      period,
      network: Network.toDto(network),
    })
  }, [type, period, network, delugeId])

  const [loadCommentsRequestState] = useAtom(model.loadCommentsRequestState)
  const noComments = AsyncStateN.matchWaiting(loadCommentsRequestState, {
    Success: (items) => items.length === 0,
    Error: () => true,
    Waiting: () => false,
  })

  return (
    <Root data-empty={noComments}>
      <Background>
        <Heading>
          <SegmentControl value={type} onChange={setType}>
            <SegmentControl.Item
              value={'comments' satisfies Comment.CommentType}
            >
              {t('comment.comments')}
            </SegmentControl.Item>
            <SegmentControl.Item
              value={'replies' satisfies Comment.CommentType}
            >
              {t('comment.replies')}
            </SegmentControl.Item>
          </SegmentControl>
          <SegmentControl
            value={period}
            onChange={setPeriod}
            disabled={noComments}
          >
            {Period.PERIODS.map((period) => (
              <SegmentControl.Item
                key={period}
                value={period}
                disabled={noComments}
              >
                {t(Period.PERIOD_LABEL_MAP[period]).toUpperCase()}
              </SegmentControl.Item>
            ))}
          </SegmentControl>
        </Heading>
        <CommentsHistory delugeId={delugeId} model={model} />
      </Background>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  padding: 16px;
  padding-bottom: 10px;

  &[data-empty='true'] {
    border-radius: 12px;
    padding: 6px;
  }
`

const Background = styled.div`
  gap: 10px;
  display: grid;
  grid-template-rows: auto 1fr;

  ${Root}[data-empty='true'] & {
    background: var(--appearance-1);
    border-radius: 12px;
  }
`

const Heading = styled.header`
  display: grid;

  grid-template-columns: auto auto;
  justify-content: space-between;

  ${Root}[data-empty='true'] & {
    height: 56px;
    padding-inline: 10px;
    display: grid;
    place-items: center;
  }
`
